import { Seo } from '@frontend/shared/web';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { IncidentDonut } from '../../components/dashboard/IncidentDonut';
import { KpiGrid } from '../../components/dashboard/KpiGrid';
import { NoteClientCard } from '../../components/dashboard/NoteClientCard';
import { TimeSeriesChart } from '../../components/dashboard/TimeSeriesChart';
import { useFilterStore } from '../../components/filters/filterStore';
import { GlobalFilters } from '../../components/filters/GlobalFilters';
import { IconSearch, IconX } from '../../components/incidents/icons';
import { Header } from '../../components/layout/Header';
import { RidesPagination } from '../../components/rides/RidesPagination';
import { RidesTable } from '../../components/rides/RidesTable';
import { useAccount } from '../../contexts/AccountContext';
import { useRides } from '../../services/api/hooks/useRides';

export const DashboardRoute = () => {
  const { can } = useAccount();
  const { entityIds, contractIds, siteIds, dateRange } = useFilterStore();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');

  // debounce search
  useEffect(() => {
    const id = window.setTimeout(() => {
      if (searchDraft !== search) {
        setSearch(searchDraft);
        setPage(1);
      }
    }, 300);
    return () => window.clearTimeout(id);
  }, [searchDraft, search]);

  const canCreateIncident = can('incidents', 'create');

  const ridesQuery = useRides({
    entityIds,
    contractIds,
    siteIds,
    dateRange,
    page,
    limit: 25,
    search: search || undefined,
    sortBy: 'pickupAt',
    sortDir: 'desc',
  });

  // Le back ne livre pas toujours `totalItems` — fallback sur `items.length`.
  const ridesTotal = ridesQuery.data?.totalItems ?? ridesQuery.data?.items?.length ?? 0;

  return (
    <>
      <Seo title="Tableau de bord — Portail extranet" />
      <Header title="Tableau de bord" subtitle="Vue synthétique de votre activité" />

      <div className="flex-1 overflow-y-auto">
        <div className="px-8 py-6 flex flex-col gap-6">
          <GlobalFilters />

          <NoteClientCard />

          <KpiGrid />

          <section className="grid gap-4 grid-cols-1 lg:grid-cols-2">
            <IncidentDonut />
            <TimeSeriesChart />
          </section>

          <section className="flex flex-col gap-3">
            <header className="flex items-baseline justify-between gap-3 flex-wrap">
              <div>
                <h2 className="text-sm font-medium text-zinc-900">Courses</h2>
                <p className="text-xs text-zinc-500 mt-0.5">
                  Toutes les courses du périmètre filtré, par défaut les 25 plus récentes.
                </p>
              </div>
              <div className="relative flex-1 max-w-xs">
                <IconSearch
                  width={14}
                  height={14}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none"
                />
                <input
                  type="text"
                  value={searchDraft}
                  onChange={e => setSearchDraft(e.target.value)}
                  placeholder="N° course ou GALAC'SI"
                  className="w-full h-8 pl-8 pr-8 text-sm bg-white border border-zinc-200 rounded-md text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-400 focus:ring-2 focus:ring-zinc-900/5 transition-colors"
                />
                {searchDraft ? (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchDraft('');
                      setSearch('');
                      setPage(1);
                    }}
                    aria-label="Effacer la recherche"
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 h-5 w-5 inline-flex items-center justify-center rounded text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
                  >
                    <IconX width={12} height={12} />
                  </button>
                ) : null}
              </div>
            </header>

            <RidesTable
              items={ridesQuery.data?.items ?? []}
              total={ridesTotal}
              isLoading={ridesQuery.isLoading}
              isFetching={ridesQuery.isFetching}
              onRowAction={
                canCreateIncident
                  ? trip => {
                      navigate(`/incidents?createForRide=${encodeURIComponent(trip.id)}`);
                    }
                  : undefined
              }
              actionLabel={canCreateIncident ? 'Signaler une réclamation' : undefined}
            />

            <RidesPagination page={page} limit={25} total={ridesTotal} onPageChange={setPage} />
          </section>
        </div>
      </div>
    </>
  );
};
